'use client';

import { Cpu, ArrowRight } from 'lucide-react';
import { trackCtaClick } from '@/lib/analytics';

/**
 * Wide Area Intelligence product CTA — funnels InventiveHQ traffic to Wide
 * Area Intelligence (wideareaai.com), Sean's edge-first AI gateway SaaS.
 * (Rebranded from "Wide Area AI"; the wideareaai.com domain is unchanged.)
 *
 * Targets two audiences:
 *  - Local-model users (Ollama / LM Studio / llama.cpp): "your GPU is an
 *    OpenAI-compatible endpoint" — the local-models variant.
 *  - People hitting API rate limits or watching token costs: "route to GPUs
 *    you own first, cloud is only a burst" — rate-limits / token-costs.
 *
 * Bundle-safe: uses a plain <a> tag (NOT next/link) so it can be imported into
 * R2-bundled tools like llm-token-counter. See CLAUDE.md "R2 Tool Bundle System".
 *
 * Destination + UTM are centralized in buildWideAreaAIUrl() — the single swap
 * point. `placement` is folded into utm_content (and the cta_name we send to
 * GA4) so we can tell which on-page slot earns the click (sidebar / inline-end
 * / sticky). Each variant lands on a message-matched wideareaai.com
 * page (/for/local-models, /for/rate-limits, /for/token-costs) — see LANDING_PATH.
 */

const WIDEAREAAI_BASE = 'https://wideareaai.com';

export type WideAreaAIVariant = 'local-models' | 'rate-limits' | 'token-costs';
export type WideAreaAIMedium = 'tool_cta' | 'blog_cta' | 'kb_cta';

interface WideAreaAICTAProps {
  /** Which product angle to pitch (controls copy + utm_campaign). */
  variant?: WideAreaAIVariant;
  /** Source page slug, for utm_content + click tracking. */
  source: string;
  /** Content type hosting the CTA — sets utm_medium so attribution can compare conversion by content type. */
  medium?: WideAreaAIMedium;
  /** Tighter spacing when embedded mid-result / in a sidebar card. */
  compact?: boolean;
  /** On-page slot label (sidebar / inline-end / tool). Folded into utm_content + cta_name for per-slot click attribution. */
  placement?: string;
}

const CAMPAIGN: Record<WideAreaAIVariant, string> = {
  'local-models': 'local-inference',
  'rate-limits': 'rate-limits',
  'token-costs': 'token-costs',
};

/** Message-matched landing page per variant. Each routes to a dedicated wideareaai.com page whose headline matches the CTA promise. NOTE: deploy the wideareaai.com /for/* pages BEFORE shipping this, or these CTAs will 404. */
const LANDING_PATH: Record<WideAreaAIVariant, string> = {
  'local-models': '/for/local-models',
  'rate-limits': '/for/rate-limits',
  'token-costs': '/for/token-costs',
};

/** Centralized destination builder — swap this to re-route all Wide Area Intelligence CTAs. */
export function buildWideAreaAIUrl(
  variant: WideAreaAIVariant,
  source: string,
  medium: WideAreaAIMedium = 'kb_cta',
  placement?: string
): string {
  const params = new URLSearchParams({
    utm_source: 'inventivehq',
    utm_medium: medium,
    utm_campaign: CAMPAIGN[variant],
    // Fold placement into utm_content so the destination property can attribute
    // the click to the exact on-page slot (e.g. how-to-use-local-models__sticky).
    utm_content: placement ? `${source}__${placement}` : source,
  });
  return `${WIDEAREAAI_BASE}${LANDING_PATH[variant]}?${params.toString()}`;
}

/** GA4 cta_name label — carries variant + placement so click reports break down by slot. */
export function wideAreaAICtaName(variant: WideAreaAIVariant, placement?: string): string {
  return `Wide Area Intelligence: ${variant}${placement ? ` [${placement}]` : ''}`;
}

function copyFor(variant: WideAreaAIVariant) {
  switch (variant) {
    case 'local-models':
      return {
        eyebrow: 'Running models locally?',
        title: 'Turn your GPU into an OpenAI-compatible endpoint',
        body:
          'Wide Area Intelligence is an edge-first AI gateway — it serves the GPU you already own over a Cloudflare Tunnel as an OpenAI-compatible endpoint, edge-caches repeated requests, and bursts to the cloud only when your node is offline. Works with any OpenAI SDK.',
        button: 'Start routing — free',
      };
    case 'rate-limits':
      return {
        eyebrow: 'Tired of rate limits?',
        title: 'Your own GPU never rate-limits you',
        body:
          'Wide Area Intelligence routes requests to GPUs you own first — free, unlimited local inference served from the edge — and bursts to the cloud (your own key) only when your node is busy or offline. OpenAI-compatible endpoint, zero code changes.',
        button: 'Start routing — free',
      };
    case 'token-costs':
      return {
        eyebrow: 'Cut your token bill',
        title: 'Stop paying per token — route to your own GPU',
        body:
          'Wide Area Intelligence is an edge-first AI gateway: repeated requests hit an edge cache, the rest run on GPUs you own for free, and the cloud is only a burst-capacity failover. OpenAI-compatible, bring your own key.',
        button: 'Start routing — free',
      };
  }
}

export default function WideAreaAICTA({
  variant = 'local-models',
  source,
  medium = 'kb_cta',
  compact,
  placement,
}: WideAreaAICTAProps) {
  const c = copyFor(variant);
  const href = buildWideAreaAIUrl(variant, source, medium, placement);

  return (
    <div
      className={`${compact ? 'p-4' : 'p-5'} rounded-lg border border-indigo-200 dark:border-indigo-800 bg-gradient-to-br from-indigo-50 to-sky-50`}
    >
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center">
            <Cpu className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold uppercase tracking-wide text-indigo-600 dark:text-indigo-400 mb-1">
            {c.eyebrow}
          </div>
          <h3 className="text-base font-bold text-gray-900 dark:text-gray-100 mb-1">{c.title}</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{c.body}</p>
          <a
            href={href}
            target="_blank"
            rel="nofollow noopener noreferrer"
            onClick={() => trackCtaClick(wideAreaAICtaName(variant, placement), medium, href)}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] text-white px-4 py-2 rounded-md font-medium text-sm hover:shadow-lg transition-all"
          >
            {c.button}
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </div>
  );
}
